// frontend/src/components/CodeEditor.tsx
import { useState, useEffect } from 'react';
import {
  Code,
  Save,
  Plus,
  Trash2,
  File,
  FileText,
  Loader2,
  AlertCircle,
  Check,
  ChevronRight,
  ChevronDown,
  Layout,
  Server,
  FileCode,
  RotateCcw,
  History,
  X,
  Clock,
  MessageSquare,
  Download,
  Upload,
} from 'lucide-react';
import { useProjectCode, useFileVersions, FileVersion } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import SyntaxHighlightedEditor from './SyntaxHighlightedEditor';

interface CodeEditorProps {
  projectId: string;
  onBuildTriggered?: () => Promise<void> | void;
  onBuildStarted?: () => void;
  isForked?: boolean;
}

type TabType = 'backend' | 'frontend';

// Files that cannot be modified in forked projects
const FORKED_RESTRICTED_FILES = ['backend/requirements.txt', 'backend/Dockerfile', 'requirements.txt', 'Dockerfile', 'backend/cloud-run.json'];

// Get language for syntax highlighting based on filename
const getLanguageFromFilename = (filename: string): 'python' | 'html' | 'css' | 'javascript' | 'markdown' | 'text' => {
  if (filename.endsWith('.py')) return 'python';
  if (filename.endsWith('.html')) return 'html';
  if (filename.endsWith('.css')) return 'css';
  if (filename.endsWith('.js')) return 'javascript';
  if (filename.endsWith('.md')) return 'markdown';
  return 'text';
};

// Build a zip file from a Record<filename, content> and trigger a download
function downloadFilesAsZip(files: Record<string, string>, zipName: string) {
  const encoder = new TextEncoder();
  // Binary files are stored in `files` with a "base64:" prefix to survive the
  // text-only JSON transport. Decode that prefix back to raw bytes here so the
  // zip contains real binary data, not the literal "base64:..." text.
  const decodeContent = (content: string): Uint8Array => {
    if (content.startsWith('base64:')) {
      const binary = atob(content.slice(7));
      const out = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
      return out;
    }
    return encoder.encode(content);
  };
  const entries = Object.entries(files).map(([name, content]) => ({
    name: encoder.encode(name),
    data: decodeContent(content),
  }));

  // Calculate total size needed
  // Each file: 30 + nameLen + dataLen (local header) + 46 + nameLen (central dir)
  // End record: 22
  let offset = 0;
  const localOffsets: number[] = [];
  for (const { name, data } of entries) {
    localOffsets.push(offset);
    offset += 30 + name.length + data.length;
  }
  const centralDirStart = offset;
  for (const { name } of entries) {
    offset += 46 + name.length;
  }
  const centralDirSize = offset - centralDirStart;
  offset += 22; // end record

  const buf = new ArrayBuffer(offset);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  let pos = 0;

  // CRC-32 lookup table
  const crcTable = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    crcTable[i] = c;
  }
  function crc32(data: Uint8Array): number {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < data.length; i++) crc = crcTable[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
  }

  // Write local file headers + data
  for (let i = 0; i < entries.length; i++) {
    const { name, data } = entries[i];
    const crc = crc32(data);
    view.setUint32(pos, 0x04034b50, true); pos += 4; // signature
    view.setUint16(pos, 20, true); pos += 2; // version needed
    view.setUint16(pos, 0, true); pos += 2; // flags
    view.setUint16(pos, 0, true); pos += 2; // compression (store)
    view.setUint16(pos, 0, true); pos += 2; // mod time
    view.setUint16(pos, 0, true); pos += 2; // mod date
    view.setUint32(pos, crc, true); pos += 4; // crc32
    view.setUint32(pos, data.length, true); pos += 4; // compressed size
    view.setUint32(pos, data.length, true); pos += 4; // uncompressed size
    view.setUint16(pos, name.length, true); pos += 2; // filename length
    view.setUint16(pos, 0, true); pos += 2; // extra field length
    bytes.set(name, pos); pos += name.length;
    bytes.set(data, pos); pos += data.length;
  }

  // Write central directory
  for (let i = 0; i < entries.length; i++) {
    const { name, data } = entries[i];
    const crc = crc32(data);
    view.setUint32(pos, 0x02014b50, true); pos += 4; // signature
    view.setUint16(pos, 20, true); pos += 2; // version made by
    view.setUint16(pos, 20, true); pos += 2; // version needed
    view.setUint16(pos, 0, true); pos += 2; // flags
    view.setUint16(pos, 0, true); pos += 2; // compression
    view.setUint16(pos, 0, true); pos += 2; // mod time
    view.setUint16(pos, 0, true); pos += 2; // mod date
    view.setUint32(pos, crc, true); pos += 4; // crc32
    view.setUint32(pos, data.length, true); pos += 4; // compressed size
    view.setUint32(pos, data.length, true); pos += 4; // uncompressed size
    view.setUint16(pos, name.length, true); pos += 2; // filename length
    view.setUint16(pos, 0, true); pos += 2; // extra field length
    view.setUint16(pos, 0, true); pos += 2; // comment length
    view.setUint16(pos, 0, true); pos += 2; // disk number start
    view.setUint16(pos, 0, true); pos += 2; // internal attrs
    view.setUint32(pos, 0, true); pos += 4; // external attrs
    view.setUint32(pos, localOffsets[i], true); pos += 4; // local header offset
    bytes.set(name, pos); pos += name.length;
  }

  // End of central directory
  view.setUint32(pos, 0x06054b50, true); pos += 4; // signature
  view.setUint16(pos, 0, true); pos += 2; // disk number
  view.setUint16(pos, 0, true); pos += 2; // central dir disk
  view.setUint16(pos, entries.length, true); pos += 2; // entries on disk
  view.setUint16(pos, entries.length, true); pos += 2; // total entries
  view.setUint32(pos, centralDirSize, true); pos += 4; // central dir size
  view.setUint32(pos, centralDirStart, true); pos += 4; // central dir offset
  view.setUint16(pos, 0, true); // comment length

  const blob = new Blob([buf], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = zipName;
  a.click();
  URL.revokeObjectURL(url);
}

// File extensions that must be treated as binary (base64-encoded for transport).
const BINARY_EXTENSIONS = new Set([
  '.icc', '.icm',          // ICC color profiles
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.ico', '.webp', '.tiff', '.tif', '.svg',
  '.woff', '.woff2', '.ttf', '.otf', '.eot',    // fonts
  '.pdf', '.zip', '.gz', '.tar', '.bz2', '.7z',  // archives & documents
  '.wasm', '.so', '.dll', '.dylib',               // binaries
  '.pyc', '.pyo', '.class',                       // compiled
  '.sqlite', '.db',                                // databases
]);

function isBinaryFile(filename: string): boolean {
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return false;
  return BINARY_EXTENSIONS.has(filename.substring(dot).toLowerCase());
}

function uint8ArrayToBase64(data: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary);
}

// Parse a zip file and return a Record<filename, content>.
// Binary files are returned with a "base64:" prefix to preserve their content
// through the text-only JSON save pipeline.
async function parseZipFile(file: File): Promise<Record<string, string>> {
  const buffer = await file.arrayBuffer();
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const decoder = new TextDecoder();
  const result: Record<string, string> = {};

  // Find the End of Central Directory record (scan backwards)
  let eocdOffset = -1;
  for (let i = buffer.byteLength - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocdOffset = i;
      break;
    }
  }
  if (eocdOffset === -1) throw new Error('Invalid zip file: could not find end of central directory');

  const centralDirOffset = view.getUint32(eocdOffset + 16, true);
  const entryCount = view.getUint16(eocdOffset + 10, true);

  // Read central directory entries to get file metadata
  let pos = centralDirOffset;
  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(pos, true) !== 0x02014b50) throw new Error('Invalid central directory entry');
    const method = view.getUint16(pos + 10, true);
    const compressedSize = view.getUint32(pos + 20, true);
    const uncompressedSize = view.getUint32(pos + 24, true);
    const nameLen = view.getUint16(pos + 28, true);
    const extraLen = view.getUint16(pos + 30, true);
    const commentLen = view.getUint16(pos + 32, true);
    const localHeaderOffset = view.getUint32(pos + 42, true);
    const name = decoder.decode(bytes.slice(pos + 46, pos + 46 + nameLen));
    pos += 46 + nameLen + extraLen + commentLen;

    // Skip directories
    if (name.endsWith('/')) continue;

    // Read data from local file header
    const localNameLen = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLen = view.getUint16(localHeaderOffset + 28, true);
    const dataOffset = localHeaderOffset + 30 + localNameLen + localExtraLen;
    const compressedData = bytes.slice(dataOffset, dataOffset + compressedSize);

    let fileData: Uint8Array;
    if (method === 0) {
      // Stored (no compression)
      fileData = compressedData;
    } else if (method === 8) {
      // Deflate - use DecompressionStream
      const ds = new DecompressionStream('deflate-raw');
      const writer = ds.writable.getWriter();
      const reader = ds.readable.getReader();
      writer.write(compressedData);
      writer.close();
      const chunks: Uint8Array[] = [];
      let totalLen = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        totalLen += value.length;
      }
      fileData = new Uint8Array(totalLen);
      let offset = 0;
      for (const chunk of chunks) {
        fileData.set(chunk, offset);
        offset += chunk.length;
      }
    } else {
      console.warn(`Skipping ${name}: unsupported compression method ${method}`);
      continue;
    }

    if (fileData.length !== uncompressedSize) {
      console.warn(`Size mismatch for ${name}: expected ${uncompressedSize}, got ${fileData.length}`);
    }

    // Binary files are base64-encoded to prevent corruption from UTF-8 decoding.
    if (isBinaryFile(name)) {
      result[name] = 'base64:' + uint8ArrayToBase64(fileData);
    } else {
      result[name] = decoder.decode(fileData);
    }
  }

  return result;
}

export default function CodeEditor({ projectId, onBuildTriggered, onBuildStarted, isForked = false }: CodeEditorProps) {
  const { session } = useAuth();
  const { files, loading, saving, error, saveFile, createFile, deleteFile, fetchCode } = useProjectCode(projectId);
  const { versions, loading: versionsLoading, fetchVersions, getVersionContent, restoreFile } = useFileVersions(projectId);
  const [activeTab, setActiveTab] = useState<TabType>('frontend');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);

  // Helper to check if a file is restricted for forked projects
  const isRestrictedFile = (filename: string): boolean => {
    if (!isForked) return false;
    return FORKED_RESTRICTED_FILES.includes(filename);
  };
  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isExpanded, setIsExpanded] = useState(true);
  const [showVersionsPanel, setShowVersionsPanel] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<FileVersion | null>(null);
  const [versionContent, setVersionContent] = useState<string | null>(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUploadZip = async (file: File) => {
    setUploading(true);
    try {
      const zipFiles = await parseZipFile(file);
      const entries = Object.entries(zipFiles);
      if (entries.length === 0) {
        alert('The zip file contains no files.');
        return;
      }

      // Check that all files have valid prefixes (backend/, frontend/, or root-level family.json)
      const isValidFile = (name: string) => name.startsWith('backend/') || name.startsWith('frontend/') || name === 'family.json';
      const invalidFiles = entries.filter(([name]) => !isValidFile(name));
      if (invalidFiles.length > 0) {
        alert(
          `The following files don't have a valid folder prefix (backend/ or frontend/) and will be skipped:\n\n${invalidFiles.map(([n]) => n).join('\n')}`
        );
      }

      const validEntries = entries.filter(([name]) => isValidFile(name));
      if (validEntries.length === 0) {
        alert('No valid files to upload.');
        return;
      }

      // Check for restricted files in forked projects
      if (isForked) {
        const restricted = validEntries.filter(([name]) => FORKED_RESTRICTED_FILES.includes(name));
        if (restricted.length > 0) {
          alert(
            `The following files cannot be modified in a forked project and will be skipped:\n\n${restricted.map(([n]) => n).join('\n')}`
          );
        }
      }

      const filesToSave = isForked
        ? validEntries.filter(([name]) => !FORKED_RESTRICTED_FILES.includes(name))
        : validEntries;

      // Skip files whose content hasn't changed (avoids unnecessary backend rebuilds)
      const changedFiles = filesToSave.filter(([name, content]) => files[name] !== content);

      if (changedFiles.length === 0) {
        alert('All files are already up to date. Nothing to save.');
        return;
      }

      // Save changed files sequentially
      let hasDependencyFile = false;
      for (const [filename, content] of changedFiles) {
        if (DEPENDENCY_FILES.includes(filename)) {
          hasDependencyFile = true;
        }
        const result = await saveFile(filename, content);
        if (result.error) {
          alert(`Error saving ${filename}: ${result.error}`);
          break;
        }
      }

      // Trigger a backend rebuild if any dependency files were modified
      if (hasDependencyFile && onBuildTriggered) {
        await onBuildTriggered();
      }

      // Refresh to pick up all changes
      await fetchCode();
    } catch (err) {
      alert(`Failed to parse zip file: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  // Organize files by category
  const organizeFiles = () => {
    const backend: string[] = [];
    const frontend: string[] = [];

    Object.keys(files).forEach((filename) => {
      if (filename.startsWith('backend/')) {
        backend.push(filename);
      } else if (filename.startsWith('frontend/')) {
        frontend.push(filename);
      } else {
        // Legacy files without folder prefix go to backend
        backend.push(filename);
      }
    });

    return { backend: backend.sort(), frontend: frontend.sort() };
  };

  const { backend, frontend } = organizeFiles();

  const getCurrentTabFiles = (): string[] => {
    switch (activeTab) {
      case 'backend':
        return backend;
      case 'frontend':
        return frontend;
      default:
        return [];
    }
  };

  // Select first file of active tab by default
  useEffect(() => {
    const tabFiles = getCurrentTabFiles();
    if (tabFiles.length > 0 && (!selectedFile || !tabFiles.includes(selectedFile))) {
      setSelectedFile(tabFiles[0]);
    }
  }, [activeTab, files]);

  // Update editor content when selected file changes
  useEffect(() => {
    if (selectedFile && files[selectedFile] !== undefined) {
      setEditedContent(files[selectedFile]);
      setHasChanges(false);
    }
  }, [selectedFile, files]);

  const handleContentChange = (value: string) => {
    setEditedContent(value);
    setHasChanges(value !== files[selectedFile!]);
    setSaveStatus('idle');
  };

  // Files that trigger a backend rebuild when modified
  const DEPENDENCY_FILES = ['backend/requirements.txt', 'backend/Dockerfile', 'requirements.txt', 'Dockerfile', 'backend/cloud-run.json'];

  const handleSave = async () => {
    if (!selectedFile || !hasChanges) return;

    setSaveStatus('saving');

    const isDependencyFile = DEPENDENCY_FILES.includes(selectedFile);

    const result = await saveFile(selectedFile, editedContent);
    console.log('CodeEditor handleSave result:', result);

    if (result.error) {
      setSaveStatus('error');
    } else {
      if (isDependencyFile && onBuildTriggered) {
        await onBuildTriggered();
      } else if (result.buildTriggered && onBuildStarted) {
        onBuildStarted();
      }
      setSaveStatus('saved');
      setHasChanges(false);
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleCreateFile = async () => {
    if (!newFileName.trim()) return;

    // Auto-prefix with current tab folder
    let filename = newFileName.trim();
    if (activeTab === 'backend' && !filename.startsWith('backend/')) {
      filename = `backend/${filename}`;
    } else if (activeTab === 'frontend' && !filename.startsWith('frontend/')) {
      filename = `frontend/${filename}`;
    }

    const result = await createFile(filename, '');
    
    if (!result.error) {
      setShowNewFileModal(false);
      setNewFileName('');
      setSelectedFile(filename);
    }
  };

  const handleDeleteFile = async (filename: string) => {
    setShowDeleteConfirm(null);
    const result = await deleteFile(filename);

    if (!result.error) {
      if (selectedFile === filename) {
        const tabFiles = getCurrentTabFiles().filter((f) => f !== filename);
        setSelectedFile(tabFiles[0] || null);
      }
    }
  };

  const handleShowVersions = async () => {
    if (!selectedFile) return;
    setShowVersionsPanel(true);
    setSelectedVersion(null);
    setVersionContent(null);
    
    // Fetch versions and auto-select the most recent one
    const fetchedVersions = await fetchVersions(selectedFile);
    if (fetchedVersions.length > 0) {
      const mostRecent = fetchedVersions[0];
      setSelectedVersion(mostRecent);
      setLoadingVersion(true);
      const result = await getVersionContent(selectedFile, mostRecent.conversation_id);
      setLoadingVersion(false);
      
      if ('content' in result) {
        setVersionContent(result.content);
      }
    }
  };

  const handleSelectVersion = async (version: FileVersion) => {
    if (!selectedFile) return;
    setSelectedVersion(version);
    setLoadingVersion(true);
    const result = await getVersionContent(selectedFile, version.conversation_id);
    setLoadingVersion(false);
    
    if ('content' in result) {
      setVersionContent(result.content);
    } else {
      setVersionContent(null);
    }
  };

  const handleRollback = async () => {
    if (!selectedVersion || !selectedFile) return;
    
    setRollingBack(true);
    const result = await restoreFile(selectedFile, selectedVersion.conversation_id);
    setRollingBack(false);
    
    if (result.success) {
      setShowVersionsPanel(false);
      setSelectedVersion(null);
      setVersionContent(null);
      await fetchCode();
    }
  };

  const formatVersionDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return diffMins + ' min ago';
    if (diffHours < 24) return diffHours + ' hour' + (diffHours > 1 ? 's' : '') + ' ago';
    if (diffDays < 7) return diffDays + ' day' + (diffDays > 1 ? 's' : '') + ' ago';
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileVersions = () => {
    return versions;
  };

  const handleInitializeCode = async () => {
    if (!confirm('This will reset all code files to defaults. Continue?')) return;
    if (!session) return;
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'init' }),
        }
      );

      if (response.ok) {
        // Reload page to sync all components
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to initialize code:', err);
    }
  };

  const getFileIcon = (filename: string) => {
    if (filename.endsWith('.py')) return <FileCode className="h-4 w-4 text-theme-code-yellow" />;
    if (filename.endsWith('.html')) return <Layout className="h-4 w-4 text-theme-code-orange" />;
    if (filename.endsWith('.css')) return <FileCode className="h-4 w-4 text-theme-code-blue" />;
    if (filename.endsWith('.js')) return <FileCode className="h-4 w-4 text-theme-code-yellow" />;
    if (filename.endsWith('.md')) return <FileText className="h-4 w-4 text-theme-code-blue" />;
    if (filename.endsWith('.txt')) return <File className="h-4 w-4 text-theme-text-muted" />;
    return <File className="h-4 w-4 text-theme-text-muted" />;
  };

  const getDisplayName = (filename: string): string => {
    // Remove folder prefix for display
    if (filename.startsWith('backend/')) return filename.substring(8);
    if (filename.startsWith('frontend/')) return filename.substring(9);
    return filename;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode; count: number }[] = [
    { id: 'frontend', label: 'Frontend', icon: <Layout className="h-4 w-4" />, count: frontend.length },
    { id: 'backend', label: 'Backend', icon: <Server className="h-4 w-4" />, count: backend.length },
  ];

  if (loading) {
    return (
      <div className="bg-theme-surface rounded-xl p-6 border border-theme-border-muted">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-accent-500 animate-spin" />
        </div>
      </div>
    );
  }

  const tabFiles = getCurrentTabFiles();

  return (
    <div className="bg-theme-surface rounded-xl overflow-hidden border border-theme-border-muted">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 border-b border-theme-border-muted cursor-pointer hover:bg-theme-surface-hover"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-theme-text-muted" />
          ) : (
            <ChevronRight className="h-5 w-5 text-theme-text-muted" />
          )}
          <Code className="h-5 w-5 text-accent-500" />
          <h3 className="text-lg font-semibold text-theme-text">Mini-App Code</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleInitializeCode();
            }}
            className="btn btn-secondary text-sm py-1.5 px-3"
            title="Reset all code files to defaults"
          >
            <RotateCcw className="h-4 w-4" />
            Reinitialize
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadFilesAsZip(files, `miniapp-${projectId.slice(0, 8)}.zip`);
            }}
            className="btn btn-secondary text-sm py-1.5 px-3"
            title="Download all code as zip"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.zip,application/zip,application/x-zip-compressed,application/octet-stream';
              input.onchange = () => {
                const file = input.files?.[0];
                if (file) handleUploadZip(file);
              };
              input.click();
            }}
            disabled={uploading}
            className="btn btn-secondary text-sm py-1.5 px-3"
            title="Upload zip to replace code files"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          </button>
          {!isExpanded && (
            <span className="text-sm text-theme-text-muted">{Object.keys(files).length} files</span>
          )}
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Tabs */}
          <div className="flex border-b border-theme-border-muted bg-theme-bg-muted">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab(tab.id);
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-accent-500 text-accent-400 bg-theme-surface'
                    : 'border-transparent text-theme-text-muted hover:text-theme-text-secondary hover:bg-theme-surface-hover'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-theme-primary-muted text-accent-400' : 'bg-theme-surface-active text-theme-text-muted'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex" style={{ height: '500px' }}>
            {/* File sidebar */}
            <div className="w-52 shrink-0 border-r border-theme-border-muted flex flex-col">
              <div className="p-2 border-b border-theme-border-muted shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNewFileModal(true);
                  }}
                  className="btn btn-secondary w-full text-sm py-1.5"
                >
                  <Plus className="h-4 w-4" />
                  New File
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto min-h-0">
                {tabFiles.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-theme-text-muted text-sm">
                      No files in this section
                    </p>
                  </div>
                ) : (
                  <ul className="py-1">
                    {tabFiles.map((filename) => (
                      <li key={filename}>
                        <button
                          onClick={() => setSelectedFile(filename)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                            selectedFile === filename
                              ? 'bg-theme-primary-muted text-accent-400'
                              : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                          }`}
                        >
                          {getFileIcon(filename)}
                          <span className="truncate flex-1">{getDisplayName(filename)}</span>
                          {selectedFile === filename && hasChanges && (
                            <span className="w-2 h-2 rounded-full bg-theme-warning" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Editor area */}
            <div className="flex-1 flex flex-col min-w-0">
              {selectedFile ? (
                <>
                  {/* Editor toolbar */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-theme-border-muted bg-theme-bg-muted gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-shrink">
                      <span className="shrink-0">{getFileIcon(selectedFile)}</span>
                      <span className="text-sm font-medium text-theme-text truncate">{getDisplayName(selectedFile)}</span>
                      {hasChanges && (
                        <span className="text-xs text-theme-warning shrink-0">(unsaved)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {saveStatus === 'saved' && (
                        <span className="flex items-center gap-1 text-xs text-theme-success shrink-0">
                          <Check className="h-3 w-3" />
                          Saved
                        </span>
                      )}
                      {saveStatus === 'error' && (
                        <span className="flex items-center gap-1 text-xs text-theme-error shrink-0">
                          <AlertCircle className="h-3 w-3" />
                          Error saving
                        </span>
                      )}
                      <button
                        onClick={handleShowVersions}
                        className="btn btn-secondary text-sm py-1.5 px-3 shrink-0"
                        title="View version history"
                      >
                        <History className="h-4 w-4" />
                        History
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={!hasChanges || saving || isRestrictedFile(selectedFile)}
                        className="btn btn-primary text-sm py-1.5 px-3 disabled:opacity-50 shrink-0"
                        title={isRestrictedFile(selectedFile) ? 'This file cannot be modified in a forked project' : undefined}
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Save
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(selectedFile)}
                        disabled={isRestrictedFile(selectedFile)}
                        className="btn btn-secondary text-sm py-1.5 px-3 text-theme-error hover:text-theme-error-muted disabled:opacity-50 shrink-0"
                        title={isRestrictedFile(selectedFile) ? 'This file cannot be deleted in a forked project' : undefined}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Code editor with syntax highlighting */}
                  <div className="flex-1 overflow-hidden relative">
                    {isRestrictedFile(selectedFile) && (
                      <div className="absolute top-0 left-0 right-0 bg-theme-warning-bg border-b border-theme-warning text-theme-warning text-xs px-3 py-2 z-10 flex items-center gap-2">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        This file cannot be modified in a forked project
                      </div>
                    )}
                    <SyntaxHighlightedEditor
                      value={editedContent}
                      onChange={handleContentChange}
                      language={getLanguageFromFilename(selectedFile)}
                      readOnly={isRestrictedFile(selectedFile)}
                      placeholder="Enter your code here..."
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-theme-text-muted">
                  <div className="text-center">
                    <Code className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a file to edit</p>
                    <p className="text-sm mt-1">or create a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Error display */}
      {error && (
        <div className="p-4 border-t border-theme-border-muted bg-theme-error-bg">
          <div className="flex items-center gap-2 text-theme-error">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* New file modal */}
      {showNewFileModal && (
        <div className="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50">
          <div className="bg-theme-surface-elevated rounded-xl p-6 w-full max-w-md mx-4 border border-theme-border-muted" style={{ boxShadow: 'var(--shadow-xl)' }}>
            <h3 className="text-lg font-semibold text-theme-text mb-4">
              Create New {activeTab === 'backend' ? 'Backend' : 'Frontend'} File
            </h3>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-theme-text-muted text-sm">{activeTab}/</span>
              <input
                type="text"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                placeholder={activeTab === 'backend' ? 'utils.py' : 'style.css'}
                className="input flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateFile();
                  if (e.key === 'Escape') setShowNewFileModal(false);
                }}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowNewFileModal(false)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFile}
                disabled={!newFileName.trim()}
                className="btn btn-primary flex-1"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50">
          <div className="bg-theme-surface-elevated rounded-xl p-6 w-full max-w-md mx-4 border border-theme-border-muted" style={{ boxShadow: 'var(--shadow-xl)' }}>
            <h3 className="text-lg font-semibold text-theme-text mb-2">Delete File</h3>
            <p className="text-theme-text-secondary mb-4">
              Are you sure you want to delete <strong>{getDisplayName(showDeleteConfirm)}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteFile(showDeleteConfirm)}
                className="btn btn-danger flex-1"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version history panel */}
      {showVersionsPanel && selectedFile && (
        <div className="fixed inset-0 bg-theme-overlay flex items-center justify-center z-50">
          <div className="bg-theme-surface-elevated rounded-xl w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col border border-theme-border-muted" style={{ boxShadow: 'var(--shadow-xl)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-theme-border-muted">
              <div className="flex items-center gap-3">
                <History className="h-5 w-5 text-accent-400" />
                <div>
                  <h3 className="text-lg font-semibold text-theme-text">Version History</h3>
                  <p className="text-sm text-theme-text-muted">{getDisplayName(selectedFile)}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowVersionsPanel(false);
                  setSelectedVersion(null);
                  setVersionContent(null);
                }}
                className="p-2 rounded-lg hover:bg-theme-surface-hover transition-colors"
              >
                <X className="h-5 w-5 text-theme-text-muted" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 flex overflow-hidden">
              {/* Version list */}
              <div className="w-80 border-r border-theme-border-muted overflow-y-auto">
                {versionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 text-accent-400 animate-spin" />
                  </div>
                ) : getFileVersions().length === 0 ? (
                  <div className="p-6 text-center">
                    <Clock className="h-10 w-10 text-theme-text-muted mx-auto mb-3" />
                    <p className="text-theme-text-muted text-sm font-medium">No versions yet</p>
                    <p className="text-theme-text-muted text-xs mt-2">
                      Versions are created automatically when the coding AI modifies this file in a conversation.
                    </p>
                    <p className="text-theme-text-muted text-xs mt-1">
                      Start a new conversation and ask Claude to make changes to see version history.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-theme-border-muted">
                    {getFileVersions().map((version) => (
                      <li key={version.conversation_id}>
                        <button
                          onClick={() => handleSelectVersion(version)}
                          className={`w-full text-left px-4 py-3 hover:bg-theme-surface-hover transition-colors ${
                            selectedVersion?.conversation_id === version.conversation_id ? 'bg-theme-surface-hover' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-3.5 w-3.5 text-theme-text-muted" />
                              <span className="text-sm font-medium text-theme-text truncate">
                                {version.title}
                              </span>
                              {version.is_manual && (
                                <span className="text-xs px-1.5 py-0.5 bg-theme-surface-active text-theme-text-secondary rounded">
                                  Manual
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-theme-text-muted">
                            {formatVersionDate(version.created_at)}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Version preview */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {selectedVersion ? (
                  <>
                    <div className="px-4 py-3 border-b border-theme-border-muted bg-theme-bg-muted flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-theme-text">
                          {selectedVersion.title}
                        </span>
                        {selectedVersion.is_manual && (
                          <span className="text-xs px-1.5 py-0.5 bg-theme-surface-active text-theme-text-secondary rounded ml-2">
                            Manual
                          </span>
                        )}
                        <span className="text-xs text-theme-text-muted ml-2">
                          {formatVersionDate(selectedVersion.created_at)}
                        </span>
                      </div>
                      <button
                        onClick={handleRollback}
                        disabled={rollingBack}
                        className="btn btn-primary text-sm py-1.5 px-3"
                      >
                        {rollingBack ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4" />
                        )}
                        Restore this version
                      </button>
                    </div>
                    <div className="flex-1 overflow-auto">
                      {loadingVersion ? (
                        <div className="flex items-center justify-center h-full">
                          <Loader2 className="h-6 w-6 text-accent-400 animate-spin" />
                        </div>
                      ) : versionContent !== null ? (
                        <pre className="p-4 text-sm text-theme-text font-mono whitespace-pre-wrap">
                          {versionContent}
                        </pre>
                      ) : (
                        <div className="flex items-center justify-center h-full text-theme-text-muted">
                          Failed to load version content
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-theme-text-muted">
                    <div className="text-center">
                      <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Select a version to preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
